"use client";

import { useState, useCallback } from "react";
import FileUploader from "./FileUploader";
import ProcessingOverlay from "./ProcessingOverlay";
import DownloadResult from "./DownloadResult";
import SignupGateModal from "./SignupGateModal";
import UsageLimitBanner from "./UsageLimitBanner";
import { useToolFetch } from "@/hooks/useToolFetch";

export default function MergePdfTool() {
  const [files, setFiles] = useState<File[]>([]);
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState<{ url: string; size: number } | null>(null);
  const [error, setError] = useState("");
  const { toolFetch, quotaError } = useToolFetch();

  const handleFilesSelected = useCallback((f: File[]) => {
    setFiles(f);
    setResult(null);
    setError("");
  }, []);

  const moveFile = useCallback((from: number, to: number) => {
    setFiles((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  }, []);

  const merge = useCallback(async () => {
    if (files.length < 2) {
      setError("Upload at least 2 PDF files");
      return;
    }
    setProcessing(true);
    setError("");

    try {
      const formData = new FormData();
      files.forEach((f) => formData.append("files", f));

      const res = await toolFetch("/api/tools/merge", { method: "POST", body: formData });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Failed to merge PDFs");
      }

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      setResult({ url, size: blob.size });
    } catch (e: any) {
      setError(e.message || "Failed to merge PDFs");
    } finally {
      setProcessing(false);
    }
  }, [files]);

  const reset = useCallback(() => {
    setFiles([]);
    setResult(null);
    setError("");
  }, []);

  if (result) {
    return <DownloadResult fileName="merged.pdf" fileSize={result.size} downloadUrl={result.url} onReset={reset} />;
  }

  if (quotaError?.gate === "signup") return <SignupGateModal quotaError={quotaError} />;

  return (
    <div className="relative space-y-6">
      {processing && <ProcessingOverlay message="Merging your PDFs..." />}
      {quotaError?.gate === "limit" && <UsageLimitBanner quotaError={quotaError} />}

      <FileUploader
        accept=".pdf"
        multiple
        maxFiles={20}
        onFilesSelected={handleFilesSelected}
        label="Drop PDF files here"
        description="Upload 2 or more PDFs to merge"
      />

      {files.length >= 2 && (
        <div>
          <p className="text-sm font-semibold text-slate-700 mb-2">
            File order (drag to reorder)
          </p>
          <div className="space-y-2">
            {files.map((f, i) => (
              <div
                key={`${f.name}-${i}`}
                draggable
                onDragStart={(e) => e.dataTransfer.setData("text/plain", String(i))}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  const from = parseInt(e.dataTransfer.getData("text/plain"));
                  moveFile(from, i);
                }}
                className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-4 py-2.5 cursor-grab active:cursor-grabbing hover:border-slate-300 transition-colors"
              >
                <svg className="h-4 w-4 text-slate-300 shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
                </svg>
                <span className="text-sm font-medium text-slate-600 flex-1 truncate">{f.name}</span>
                <span className="text-xs text-slate-400">{(f.size / 1024 / 1024).toFixed(1)} MB</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {error && (
        <p className="text-sm text-red-600">{error}</p>
      )}

      {files.length >= 2 && (
        <button onClick={merge} disabled={processing} className="btn-primary w-full py-3">
          Merge {files.length} PDFs
        </button>
      )}
    </div>
  );
}
